'use client'

;!function(){try{var e="undefined"!=typeof globalThis?globalThis:"undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { clsx } from 'clsx/lite'

import { AppUiVisual } from '../app-ui-visual'
import {
  CURRENT_MONTH_INDEX,
  MONTH_LOAD_CEILING,
  MONTH_TONE_COLOR,
  monthTone,
  YEAR_ACTIVITIES,
  YEAR_MONTH_LOAD,
  yearActivityDone,
  type MonthLoad,
  type MonthTone,
} from '../demo-data'
import { useVisualCopy, type VisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, HeaderStat } from './app-screen'
import styles from './progress-visual.module.css'
import { useScenario } from './use-scenario'

const AXIS_MAX = MONTH_LOAD_CEILING
const AXIS_LABELS = [AXIS_MAX, AXIS_MAX / 2, 0]

/**
 * Activities the running month gains on the closing beat — the "houdt zichzelf bij" moment. The
 * listed `done` is where it lands, so the resting state is the canonical figure and only the step
 * before it is animated.
 *
 * 140, not 60: at sixty the bar moved three percentage points, which at this size is a bar not
 * moving. The step has to be visible to be the thing the beat is for.
 */
const LIVE_STEP = 140

const TOTAL_PLANNED = YEAR_MONTH_LOAD.reduce((sum, row) => sum + row.total, 0)

/** Year-to-date share of everything the chart is currently showing. */
function yearPctShown(currentMonthDone: number, aprilResolved: boolean, reached: number): number {
  const done = YEAR_MONTH_LOAD.reduce(
    (sum, row, index) => sum + monthShown(row, index, currentMonthDone, aprilResolved, reached),
    0,
  )
  return Math.round((done / TOTAL_PLANNED) * 100)
}

// The Jaarrekening counter comes off the shared year activities, so the footer can never claim a
// different completion than the year board does.
const JAARREKENING = YEAR_ACTIVITIES[0]

/**
 * A month's finished count once the chart has reached it. April is the one closed month still
 * carrying open work — amber, where its neighbours are green — and finishing it near the end is
 * what gives the loop an ending instead of simply stopping.
 */
function monthDone(
  row: { done: number; total: number },
  index: number,
  currentDone: number,
  aprilResolved: boolean,
): number {
  if (index === APRIL_INDEX && aprilResolved) return row.total
  return index === CURRENT_MONTH_INDEX ? currentDone : row.done
}

/** What a month shows at this beat: its figure if the year has reached it, nothing if it has not. */
function monthShown(
  row: { done: number; total: number },
  index: number,
  currentDone: number,
  aprilResolved: boolean,
  reached: number,
): number {
  return index < reached ? monthDone(row, index, currentDone, aprilResolved) : 0
}

/** Everything the chart is showing as finished, at a given beat. */
function shownDone(currentDone: number, aprilResolved: boolean, reached: number): number {
  return YEAR_MONTH_LOAD.reduce(
    (sum, row, index) => sum + monthShown(row, index, currentDone, aprilResolved, reached),
    0,
  )
}

/**
 * How far the Jaarrekening counter has got: the progress the chart is showing, normalised against
 * where it ends up.
 *
 * Derived from the bars rather than from a ramp of its own — the two used to be separate tables kept
 * in step by hand, and the counter then stopped climbing two beats before the chart did. Anything
 * that moves a bar now moves the counter with it, including the closing beat, where April resolves
 * and the running month takes its last activities.
 */
const jaarrekeningShare = (currentDone: number, aprilResolved: boolean, reached: number) =>
  FINAL_DONE === 0 ? 0 : shownDone(currentDone, aprilResolved, reached) / FINAL_DONE

const jaarrekeningRampLabel = (t: VisualCopy, share: number) =>
  `${Math.round(yearActivityDone(JAARREKENING) * share)} / ${JAARREKENING.items} ${t.word.completed}`

/**
 * How long each beat stays on screen. The year fills across beats 1–6 rather than arriving; beat 6
 * is held, being both the story's ending and the frame the section rests on, and beat 7 only carries
 * the wrap back and passes quickly.
 */
const BEAT_HOLDS = [700, 900, 900, 900, 900, 900, 1600, 500] as const

const RESET_BEAT = 7 // final beat reads as the empty start, so the wrap has nothing to snap
const REST_BEAT = 6 // SSR / reduced-motion snapshot — the year closed, every bar and stat final
// The running month takes its last activities on the same beat April resolves, so the closing beat
// moves two bars and both counters instead of only recolouring one. Landing it earlier left the
// final beat with nothing to add but a colour.
const LIVE_STEP_FROM = 5

/**
 * How far into the year the chart has got, per beat: months 0..n-1 stand at their real figure, the
 * rest are empty.
 *
 * A year fills month by month, and this one now does. Every bar used to grow at once on a shared
 * ramp — twelve columns all at 40%, then all at 62% — which is a chart being drawn rather than a
 * year being worked. The closed months arrive together on the first beat, because by the time
 * anyone opens this they are long finished; then each following month lands on its own beat, up to
 * and including the one the office is in, which comes in at its real share rather than part of it.
 */
const FILLED_THROUGH: readonly number[] = [
  0, // 0  empty
  4, // 1  jan–apr, the closed months, at once
  5, // 2  mei
  6, // 3  jun
  CURRENT_MONTH_INDEX + 1, // 4  jul, the running month, whole
  CURRENT_MONTH_INDEX + 1, // 5  held
  CURRENT_MONTH_INDEX + 1, // 6  april's open work resolves; the resting frame
  0, // 7  the wrap
]

const filledThrough = (effBeat: number) => FILLED_THROUGH[effBeat] ?? 0

/** April's remaining work is finished before the year settles, so the last thing to change is a
 *  colour rather than the whole chart arriving. */
const APRIL_INDEX = 3
const APRIL_RESOLVED_FROM = 5

/**
 * What the chart shows once it has finished filling — the figure the counter has to arrive at.
 *
 * Below `APRIL_INDEX` deliberately: it runs at module evaluation and reaches `monthDone`, which
 * reads that constant. Declared above it, the module threw on the server before it rendered a line.
 */
const FINAL_DONE = shownDone(YEAR_MONTH_LOAD[CURRENT_MONTH_INDEX].done, true, CURRENT_MONTH_INDEX + 1)

/** One month column: a track sized to that month's workload, filled from the bottom by its share.
 *  `done` is what to show right now — zero for a month the year has not reached yet. */
function MonthBar({
  row,
  done,
  delayMs,
  tone,
}: {
  row: MonthLoad
  done: number
  delayMs: number
  /** Read off where the month ends up, not off the filling height, so a green month does not run
   *  through amber and blue on its way up. */
  tone: MonthTone
}) {
  return (
    <div className="flex h-full min-w-0 flex-1 flex-col justify-end">
      <div
        className="flex w-full flex-col justify-end overflow-hidden rounded-t-[3px]"
        style={{ height: `${(row.total / AXIS_MAX) * 100}%`, background: 'var(--au-track)' }}
      >
        <div
          className={clsx(styles.bar, 'w-full')}
          style={{
            height: `${(done / row.total) * 100}%`,
            background: MONTH_TONE_COLOR[tone],
            transitionDelay: `${delayMs}ms`,
          }}
        />
      </div>
    </div>
  )
}

/**
 * Voortgang — the book year's month planning as a bar chart: each column is as tall as that month's
 * workload (the quarter months carry the quarterly VAT filers on top of the monthly ones, so they
 * run to roughly double), filled by how much of it is finished, and coloured by state — green when
 * a month is complete, red while a closed month still has work open, blue for the running month,
 * bare track for the months ahead. Mid-loop the running month gains activities and both stats tick
 * up. `beat === null` (SSR, no-JS, reduced motion) renders the resolved rest state.
 */
export function ProgressVisual({ className }: { className?: string }) {
  const { t } = useVisualCopy()
  const { ref, beat } = useScenario(BEAT_HOLDS, { restBeat: REST_BEAT })
  const raw = beat ?? REST_BEAT
  const effBeat = raw >= RESET_BEAT ? 0 : raw

  const reached = filledThrough(effBeat)
  const stepped = effBeat >= LIVE_STEP_FROM
  // April's open work is finished on the closing beat: the one month that was amber goes green.
  const aprilResolved = effBeat >= APRIL_RESOLVED_FROM
  const currentDone = YEAR_MONTH_LOAD[CURRENT_MONTH_INDEX].done - (stepped ? 0 : LIVE_STEP)
  const share = jaarrekeningShare(currentDone, aprilResolved, reached)

  return (
    <AppUiVisual label={t.screen.progress.caption} className={className}>
      <AppScreen>
        <div ref={ref} data-beat-schedule="progress" className={clsx(styles.root, 'flex h-full flex-col')}>
          <AppHeader
            title={t.screen.jaarplanning.title}
            detail={t.period.closingBookYear}
            right={
              <span className={styles.counter}>
                {/* The figure is the chart's own sum, so the header cannot claim a share the columns
                    under it do not add up to. */}
                <HeaderStat
                  value={`${Math.round(yearPctShown(currentDone, aprilResolved, reached))}%`}
                  label={t.screen.progress.thisYear}
                />
              </span>
            }
          />

          <div className="flex min-h-0 flex-1 flex-col px-3 pt-3 pb-2">
            <div className={clsx(styles.plot, 'min-h-0 flex-1')}>
              {/* The scale sits outside the plot, so the bars start at the same left edge as the
                  month labels under them. */}
              <div className={styles.axis}>
                {AXIS_LABELS.map((value) => (
                  <span key={value} className="leading-none">
                    {value}
                  </span>
                ))}
              </div>

              {/* No gridlines behind the bars: the axis marks the scale, and ruled paper made a
                  chart of twelve coloured columns read as a table. */}
              <div className={clsx(styles.plot, 'relative min-h-0 flex-1 items-end')}>
                {YEAR_MONTH_LOAD.map((row, index) => {
                  const done = monthDone(row, index, currentDone, aprilResolved)
                  return (
                    <MonthBar
                      key={row.month}
                      row={row}
                      done={monthShown(row, index, currentDone, aprilResolved, reached)}
                      delayMs={index < 4 ? index * 60 : 0}
                      tone={monthTone(index, { ...row, done })}
                    />
                  )
                })}
              </div>
            </div>

            {/* Same axis spacer and gaps as the row above, so every label lands under its column. */}
            <div className={clsx(styles.plot, 'pt-1.5')}>
              <span className={styles.axis} />
              <div className={clsx(styles.labels, 'flex-1')}>
                {YEAR_MONTH_LOAD.map((row, index) => {
                  const done = monthDone(row, index, currentDone, aprilResolved)
                  const shown = monthShown(row, index, currentDone, aprilResolved, reached)
                  const tone = monthTone(index, { ...row, done })
                  return (
                    <div key={row.month} className="flex min-w-0 flex-1 flex-col items-center gap-px">
                      <span className={styles.monthLabel}>{t.monthsShort[index]}</span>
                      <span className={styles.pct} style={{ color: MONTH_TONE_COLOR[tone] }}>
                        {Math.round((shown / row.total) * 100)}%
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5 border-t px-3 py-2.5" style={{ borderColor: 'var(--au-hairline)' }}>
            <div className="flex items-center justify-between gap-2">
              <span
                className="text-[9px] font-bold tracking-[0.03em] whitespace-nowrap"
                style={{ color: 'var(--au-text2)' }}
              >
                {t.yearActivity[JAARREKENING.key].label.toUpperCase()}
              </span>
              <b className="shrink-0 text-[11px] font-bold tabular-nums" style={{ color: 'var(--au-accent)' }}>
                {jaarrekeningRampLabel(t, share)}
              </b>
            </div>
            <div className="h-2 overflow-hidden rounded-full" style={{ background: 'var(--au-track)' }}>
              <div
                className={clsx(styles.bar, 'h-full rounded-full')}
                style={{
                  width: `${JAARREKENING.donePct * share}%`,
                  background: 'var(--au-accent)',
                }}
              />
            </div>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
